import React, { useEffect, useRef, useState } from 'react';
import { Check, FileDown, FileUp, Trash2, Upload } from 'lucide-react';
import { useData } from '../../App';
import { Button, Modal } from '../UI';
import { HrChecklistEvent, HrDocKind, HrDocStatus, HrDocument } from '../../types';
import { api } from '../../services/api';
import { formatDay } from '../../utils/format';
import { downloadFile } from '../billing/Setup';
import { errorText } from '../billing/shared';
import { confirmDialog } from '../Dialogs';

// Кадровые документы сотрудника (22.10.2026): чек-листы по событиям, статусы (сгенерирован → отправлен на подпись →
// подписан — скан загружен), «оригинал у бухгалтера», свои шаблоны вместо встроенных.

export const STATUS_LABEL: Record<HrDocStatus | 'MARKED' | 'MISSING', string> = {
    DRAFT: 'черновик', GENERATED: 'сгенерирован', SENT: 'на подписи', SIGNED: 'подписан', MARKED: 'отмечено', MISSING: 'нет',
};
const STATUS_TONE: Record<HrDocStatus | 'MARKED' | 'MISSING', string> = {
    DRAFT: 'bg-slate-100 text-slate-600', GENERATED: 'bg-blue-50 text-blue-700', SENT: 'bg-amber-50 text-amber-800',
    SIGNED: 'bg-emerald-50 text-emerald-700', MARKED: 'bg-emerald-50 text-emerald-700', MISSING: 'bg-red-50 text-red-700',
};
export const StatusChip: React.FC<{ status: HrDocStatus | 'MARKED' | 'MISSING' }> = ({ status }) =>
    <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;

// --- Чек-листы: что нужно по событию, чего не хватает (красным) ---
export const HrChecklist: React.FC<{ cpId: string; revision: number; onCreate: (kind: HrDocKind, transactionId?: string) => void }> =
    ({ cpId, revision, onCreate }) => {
    const { showNotification } = useData();
    const [events, setEvents] = useState<HrChecklistEvent[]>([]);
    useEffect(() => { api.hrChecklist(cpId).then(setEvents).catch(() => setEvents([])); }, [cpId, revision]);
    if (events.length === 0) return null;
    const mark = async (key: string, done: boolean) => {
        try { setEvents(await api.setHrMark(cpId, key, done)); } catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <div className="space-y-2">
            {events.map(ev => {
                const missing = ev.items.filter(i => i.required && i.status === 'MISSING').length;
                return (
                    <div key={ev.event + ev.title} className="rounded border border-slate-200 p-2">
                        <div className="flex items-center gap-2 text-xs font-semibold mb-1">
                            <span>{ev.title}</span>
                            <span className={missing ? 'text-red-700' : 'text-emerald-700'}>{missing ? `не хватает: ${missing}` : 'всё есть'}</span>
                        </div>
                        <ul className="space-y-0.5 text-xs">
                            {ev.items.map(i => (
                                <li key={i.key} className="flex flex-wrap items-center gap-2">
                                    <span className={`min-w-0 flex-1 break-words ${i.status === 'MISSING' && i.required ? 'text-red-700' : ''}`}>
                                        {i.title}{!i.required && <span className="text-slate-400"> (необязательно)</span>}
                                        {i.number && <span className="text-slate-400"> №{i.number}</span>}
                                    </span>
                                    <StatusChip status={i.status} />
                                    {i.kind && i.status === 'MISSING' && (
                                        <button className="text-blue-600 hover:underline" onClick={() => onCreate(i.kind!, ev.transactionId)}>сформировать</button>
                                    )}
                                    {!i.kind && (
                                        <label className="inline-flex items-center gap-1 cursor-pointer text-slate-600">
                                            <input type="checkbox" checked={i.status === 'MARKED'} onChange={e => mark(i.key, e.target.checked)} /> сделано
                                        </label>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
};

// --- Строка документа: DOCX, статус, «на подпись», скан, оригинал у бухгалтера ---
export const HrDocRow: React.FC<{ d: HrDocument; onChanged: (d?: HrDocument) => void; onDelete: () => void }> = ({ d, onChanged, onDelete }) => {
    const { showNotification } = useData();
    const fileRef = useRef<HTMLInputElement>(null);
    const run = async (fn: () => Promise<HrDocument | unknown>, ok?: string) => {
        try { const r = await fn(); if (ok) showNotification(ok, 'success'); onChanged(r && typeof r === 'object' && 'id' in (r as any) ? r as HrDocument : undefined); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <div className="py-1.5 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-slate-500">{formatDay(d.date)}</span>
                <span className="min-w-0 flex-1 break-words">{d.title} №{d.number}</span>
                <StatusChip status={d.status} />
                <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5" title="Скачать черновик для печати"
                    onClick={() => run(async () => downloadFile((await api.hrDocumentFile(d.id)).fileName))}><FileDown size={12} /> DOCX</button>
                <button className="text-slate-400 hover:text-red-600" title="Удалить документ"
                    onClick={async () => (await confirmDialog(`Удалить «${d.title}» №${d.number}?`)) && onDelete()}><Trash2 size={12} /></button>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-[4.5rem] text-[11px] text-slate-600">
                {d.status !== 'SIGNED' && (
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={!!d.sentAt} onChange={e => run(() => api.setHrDocStatus(d.id, { sent: e.target.checked }))} />
                        отправлен на подпись{d.sentAt ? ` ${formatDay(d.sentAt)}` : ''}
                    </label>
                )}
                {d.scanFile ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                        <button className="text-emerald-700 hover:underline inline-flex items-center gap-0.5" onClick={() => downloadFile(d.scanFile!)}>
                            <Check size={12} /> скан подписанного</button>
                        <span className="text-slate-400">{d.scanUploadedBy}{d.scanUploadedAt ? `, ${formatDay(d.scanUploadedAt.slice(0, 10))}` : ''}</span>
                        <button className="text-slate-400 hover:text-red-600" title="Убрать скан"
                            onClick={async () => (await confirmDialog('Убрать скан? Документ снова будет «не подписан».')) && run(() => api.deleteHrScan(d.id))}><Trash2 size={11} /></button>
                    </span>
                ) : (
                    <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5" onClick={() => fileRef.current?.click()}>
                        <Upload size={12} /> загрузить подписанный скан</button>
                )}
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.webp" className="hidden"
                       onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => api.uploadHrScan(d.id, f), 'Скан загружен'); }} />
                <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={d.originalAtAccountant} onChange={e => run(() => api.setHrDocStatus(d.id, { originalAtAccountant: e.target.checked }))} />
                    оригинал у бухгалтера
                </label>
            </div>
        </div>
    );
};

// --- Шаблоны: встроенные или свои (например, от бухгалтера) ---
export const HrTemplatesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { showNotification } = useData();
    const [rows, setRows] = useState<{ kind: HrDocKind; title: string; custom: boolean }[]>([]);
    const [uploading, setUploading] = useState<HrDocKind | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const load = () => api.hrTemplates().then(setRows).catch(() => setRows([]));
    useEffect(() => { load(); }, []);
    const run = async (fn: () => Promise<unknown>, ok?: string) => {
        try { await fn(); if (ok) showNotification(ok, 'success'); load(); } catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title="Шаблоны кадровых документов">
            <div className="space-y-2 text-xs">
                <p className="text-slate-500">Скачайте шаблон, поправьте в Word и загрузите обратно — документы этого вида будут формироваться по нему.
                    Поля пишутся в двойных фигурных скобках, как в скачанном файле: {'{{ employee_fio }}'}, {'{{ salary }}'}…</p>
                <div className="divide-y divide-slate-100">
                    {rows.map(r => (
                        <div key={r.kind} className="flex flex-wrap items-center gap-2 py-1.5">
                            <span className="min-w-0 flex-1 break-words">{r.title}</span>
                            <span className={`text-[10px] px-1.5 rounded ${r.custom ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>{r.custom ? 'свой' : 'встроенный'}</span>
                            <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5" onClick={() => run(async () => downloadFile((await api.hrTemplateFile(r.kind)).fileName))}>
                                <FileDown size={12} /> скачать</button>
                            <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5" onClick={() => { setUploading(r.kind); fileRef.current?.click(); }}>
                                <FileUp size={12} /> загрузить свой</button>
                            {r.custom && <button className="text-slate-500 hover:text-red-600" onClick={() => run(() => api.resetHrTemplate(r.kind), 'Вернули встроенный шаблон')}>вернуть встроенный</button>}
                        </div>
                    ))}
                </div>
                <input ref={fileRef} type="file" accept=".docx" className="hidden" onChange={e => {
                    const f = e.target.files?.[0]; e.target.value = '';
                    if (f && uploading) run(() => api.uploadHrTemplate(uploading, f), 'Шаблон загружен');
                }} />
                <div className="flex justify-end pt-2 border-t"><Button size="sm" variant="secondary" onClick={onClose}>Закрыть</Button></div>
            </div>
        </Modal>
    );
};

// --- Перевести самозанятого в штат: та же карточка → «Сотрудник», комплект приёма, расторжение ГПХ ---
export const ToStaffModal: React.FC<{ cp: import('../../types').Counterparty; onClose: () => void; onDone: () => void }> = ({ cp, onClose, onDone }) => {
    const { state, showNotification, refreshData } = useData();
    const ips = state.organizations.filter(o => o.type === 'ENTREPRENEUR');
    const [f, setF] = useState({ organizationId: ips[0]?.id || state.organizations[0]?.id || '', hireDate: new Date().toISOString().slice(0, 10),
                                 position: cp.position || '', salary: '', tabNumber: '', contractNumber: '', orderNumber: '', militaryLiable: false, terminateGph: true });
    const [busy, setBusy] = useState(false);
    const set = (k: string, v: unknown) => setF(x => ({ ...x, [k]: v }));
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.toStaff(cp.id, { ...f, salary: Number(f.salary), tabNumber: f.tabNumber || undefined,
                                                 contractNumber: f.contractNumber || undefined, orderNumber: f.orderNumber || undefined });
            showNotification(`${cp.name} — в штате с ${formatDay(f.hireDate)}: ${r.documents.map(d => `${d.title} №${d.number}`).join(', ')}`, 'success');
            refreshData(); onDone(); onClose();
        } catch (err) { showNotification(errorText(err), 'error'); }
        setBusy(false);
    };
    const inp = (k: keyof typeof f, label: string, type = 'text', placeholder?: string, required = false) => (
        <label className="block text-xs"><span className="text-slate-600">{label}</span>
            <input className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" type={type} value={String(f[k] ?? '')} placeholder={placeholder}
                   required={required} onChange={e => set(k, e.target.value)} /></label>
    );
    return (
        <Modal isOpen onClose={onClose} title={`Перевести в штат — ${cp.name}`}>
            <form onSubmit={submit} className="space-y-3 text-sm">
                <label className="block text-xs"><span className="text-slate-600">Организация (ИП)</span>
                    <select className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" value={f.organizationId} onChange={e => set('organizationId', e.target.value)}>
                        {state.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select></label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {inp('hireDate', 'Дата приёма', 'date', undefined, true)}
                    {inp('position', 'Должность', 'text', 'Монтажник', true)}
                    {inp('salary', 'Оклад (начислено), ₽', 'number', undefined, true)}
                    {inp('tabNumber', 'Табельный номер', 'text', '00004')}
                    {inp('contractNumber', '№ трудового договора', 'text', 'авто')}
                    {inp('orderNumber', '№ приказа о приёме', 'text', 'авто')}
                </div>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.militaryLiable} onChange={e => set('militaryLiable', e.target.checked)} /> Военнообязанный (сообщить в военкомат — 2 недели)</label>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.terminateGph} onChange={e => set('terminateGph', e.target.checked)} /> Расторгнуть договоры ГПХ днём раньше (соглашение о расторжении)</label>
                <p className="text-[11px] text-slate-500">Сформируются: трудовой договор (типовая форма микропредприятия), приказ Т-1, согласие на обработку ПДн, заявление о перечислении
                    зарплаты на счёт (реквизиты — из карточки). Карточка станет «Сотрудник», прежний тип сохранится в истории. После даты приёма выплаты ему
                    как самозанятому не проводятся ни с одного ИП — только зарплатой.</p>
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button type="submit" size="sm" disabled={busy}>Перевести в штат</Button>
                </div>
            </form>
        </Modal>
    );
};
